import { useQuery } from '@tanstack/react-query';
import { pagesService } from '../services/pagesService';
import { useSiteStore } from '../store/site.store';

export interface PageOption {
  id: number;
  url: string;
  label: string;
}

/**
 * Common hook to prepopulate pages list across all analytics pages
 * Used by: Anomalies, OCI Scores, Momentum, Opportunity, SERP Analysis, GA4 Integration, Recommendations
 * 
 * Now uses the SELECTED site from global store, not sites[0]
 */
export function usePagesList() {
  // Get the selected site from global store
  const selectedSite = useSiteStore(state => state.selectedSite);
  const siteId = selectedSite?.id;
  
  console.log('📋 [usePagesList] Hook called with siteId:', siteId, 'from selectedSite:', selectedSite?.name);

  // Get pages for the SELECTED site
  const { data: pagesData, isLoading: loading, error: queryError, isFetching } = useQuery({
    queryKey: ['pages-for-site', siteId],
    queryFn: async () => {
      if (!siteId) {
        console.log('❌ [usePagesList] Skipped: no siteId');
        return [];
      }
      console.log('📄 [usePagesList] Fetching pages for site:', siteId);
      console.log('📄 [usePagesList] API call: /pages/site/' + siteId);
      const data = await pagesService.getPagesBySite(siteId);
      console.log('✅ [usePagesList] Pages loaded:', data.length, 'pages');
      return data.map((p: any) => ({
        id: p.id,
        url: p.url || `Page #${p.id}`,
        label: p.url || `Page #${p.id}`,
      }));
    },
    enabled: !!siteId,
    staleTime: 0, // Force fresh fetch
    gcTime: 5 * 60 * 1000,
    refetchOnMount: true
  });

  console.log('📋 [usePagesList] Status:', { siteId, loading, isFetching, pagesCount: pagesData?.length || 0 });

  return { 
    pages: pagesData || [], 
    loading, 
    error: queryError?.message || null 
  };
}
